import { ReactNode } from "react";
import KuikaButton, {
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../../ui-kit/kuika-button/kuika-button";
import KuikaPageHeader from "../../ui-kit/kuika-page-header/kuika-page-header";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import "./code-editor-shell.scss";

/**
 * The bar every code editor opens with: what the file is called, the way to its siblings, and
 * the things you can do to it.
 *
 * The name row is the suite's own: the switcher chip, a pencil beside it, and — while renaming
 * — the bound field with confirm and abort (`CodeEditorRenameTitle`). It used to be an input
 * that was always open, sized like a heading, with the origin in a chip after it; that made the
 * code editors the one bar in the suite whose name was a form field, and the origin chip said
 * again what the switcher already does. The host draws the row through `renderTitle` because
 * the field binds to the HOST's antd form, and the name it commits lives in the host's state.
 *
 * The one exception is the UI Design dialog (`sql-action-modal`): it keeps the always-open field
 * and the datasource beside it, because a dialog has no switcher to name the datasource for it.
 */
export interface ICodeEditorHeaderProps {
  /** The record's name. `KuikaPageHeader` takes it as the bar's title even when `renderTitle` draws the row. */
  title: string;
  /** The name row. Drawn inside the host's `<Form>`, so a field in it binds. */
  renderTitle: () => ReactNode;
  /** Optional status chip between the name and the buttons. */
  status?: ReactNode;
  /**
   * The run control, for an editor that has one thing to run. The language actions run a
   * chosen function from their test console instead, so they leave these unset and the
   * button is not drawn.
   */
  runLabel?: string;
  isRunning?: boolean;
  onRun?: () => void;
  saveLabel: string;
  /** The legacy busy flag: a spinner before the label. Ignored when `saveStatus` is given. */
  isSaving?: boolean;
  /**
   * Where the save stands (`KuikaButton` `status`): the label rolls to "Saving", then "Saved" or
   * "Try again", and the button's width follows it. The editor is what the reader is looking at,
   * so the button is the confirmation — see `EditorSaveStatus`, which owns the order.
   *
   * Named `saveStatus` because `status` above is this bar's status CHIP, which is a node.
   */
  saveStatus?: KuikaButtonStatus;
  saveStatusLabels?: IKuikaButtonStatusLabels;
  isSaveDisabled: boolean;
  /**
   * Hides the save button outright. The C# action has nothing to create until a template is
   * chosen, and a disabled button there reads as "broken" rather than "not yet".
   */
  isSaveHidden?: boolean;
  onClose: () => void;
  /**
   * How leaving is drawn. `back` is the panel's own control, on the left, like every other
   * editor; `close` is the X a DIALOG needs, on the right, because a dialog has no list
   * behind it to go back to.
   */
  closeVariant?: "back" | "close";
  /** What the back control says it returns to: "Back to Datasources". */
  backLabel?: string;
  closeTestId: string;
  saveTestId: string;
  runTestId?: string;
}

export const CodeEditorHeader = (props: ICodeEditorHeaderProps): JSX.Element => {
  const isDialog: boolean = props.closeVariant === "close";

  const actions: ReactNode = (
    <>
      {props.status}
      {props.onRun && (
        <KuikaButton
          type="secondary"
          size="md"
          className="kuika__code-editor__header__run"
          loading={props.isRunning}
          onClick={props.onRun}
          data-testid={props.runTestId}
          withIconButton={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5l11 7-11 7z" />
            </svg>
          }
        >
          {props.runLabel}
        </KuikaButton>
      )}
      {!props.isSaveHidden && (
        <KuikaButton
          size="md"
          htmlType="submit"
          loading={props.isSaving}
          status={props.saveStatus}
          statusLabels={props.saveStatusLabels}
          disabled={props.isSaveDisabled}
          data-testid={props.saveTestId}
        >
          {props.saveLabel}
        </KuikaButton>
      )}
      {isDialog && (
        <KuikaButton
          isIcon
          type="tertiary"
          color="neutral"
          size="sm"
          aria-label="Close"
          onClick={props.onClose}
          data-testid={props.closeTestId}
        >
          <KuikaSvg name="x" width={16} height={16} />
        </KuikaButton>
      )}
    </>
  );

  return (
    <KuikaPageHeader
      variant="bar"
      className="kuika__code-editor__header"
      title={props.title}
      onBack={isDialog ? undefined : props.onClose}
      backLabel={props.backLabel ?? "Back"}
      backTestId={props.closeTestId}
      renderTitle={props.renderTitle}
      actions={actions}
    />
  );
};
